/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { asError, errorMessage } from '@/util/errorUtil'
import { createPersistentItem, logDebug, logWarn } from '@amazon/vinyl-util'
import { gqlFetch } from '@/service/gql'
import { gql } from '@amzn/amp-playback'
import { getAuth } from '@/auth/authRef'
import { createLocalOverlay } from '@/util/localOverlay'
import { isFeedStubEnabled } from '@/blackbolt/feedStubFlag'
import { isKnownStubRejection as isKnownStubRejectionWith } from '@/blackbolt/stubRejection'
import { ctx } from '@/util/context'

const { target } = ctx('chatMessageFeedback')

/**
 * Rating value the caller supplies when a user taps the thumbs-up /
 * thumbs-down button on a settled agent bubble. Mapped to the Firefly
 * `submitFeedback(rating: Float)` argument via a 5-point scale (thumbs
 * up → 5, thumbs down → 1) so the wire keeps its numeric contract.
 */
export type MessageFeedbackRating = 'up' | 'down'

// One remembered rating per agent message, keyed by message id. A single
// stored value per message is what makes up/down mutually exclusive: writing
// one rating overwrites the other, so a bubble can never hold both at once.
// The transcript full-rebuilds on every stream tick, so the button's pressed
// state can only survive that rebuild (and a reload) by reading back from here
// rather than from an in-DOM flag. TTL matches the feed feedback overlay; the
// keyspace is bulk-cleared on sign-out (see `clearMessageFeedbackOverlay`).
const MESSAGE_FEEDBACK_TTL_MS = 30 * 24 * 60 * 60 * 1000
const messageFeedbackOverlay = createLocalOverlay<string, MessageFeedbackRating>({
    persistence: createPersistentItem({
        key: 'overlay:chatMessageFeedback',
        defaultValue: {},
    }),
    validateValue: (v): v is MessageFeedbackRating => v === 'up' || v === 'down',
})

/** The remembered rating for a message, or `undefined` if it was never rated. */
export function readMessageFeedback(messageId: string): MessageFeedbackRating | undefined {
    return messageFeedbackOverlay.read(messageId)
}

/** Remember the user's rating for a message (overwrites any prior rating). */
export function writeMessageFeedback(messageId: string, rating: MessageFeedbackRating): void {
    messageFeedbackOverlay.write(messageId, rating, { ttlMs: MESSAGE_FEEDBACK_TTL_MS })
}

/** Forget a message's rating — used when the user toggles the active thumb off. */
export function clearMessageFeedback(messageId: string): void {
    messageFeedbackOverlay.clear(messageId)
}

/**
 * Drop every remembered message rating. Called from sign-out teardown so one
 * account's thumb state never bleeds into the next on a shared machine.
 */
export function clearMessageFeedbackOverlay(): void {
    messageFeedbackOverlay.clearAll()
}

/**
 * Firefly-side error codes / message fragments that indicate "the id
 * you sent isn't a real entity" — expected outcomes for dev-stub
 * fixtures and heartbeat messages whose `id` isn't a real Firefly
 * identifier. When we see these we stay quiet — the "request failed,
 * will not retry" toast is not user-actionable here.
 */
const STUB_REJECTION_RULES = {
    codes: new Set(['NOT_FOUND', 'INVALID_INPUT', 'BAD_REQUEST']),
    messageFragments: ['not found', 'unknown', 'invalid', 'no data available'],
} as const

const submitFeedbackMutation = gql(`
    mutation submitFeedback(
        $id: String!
        $rating: Float!
        $feedbackText: String
    ) {
        submitFeedback(
            id: $id
            rating: $rating
            feedbackText: $feedbackText
        ) {
            success
        }
    }
`)

/**
 * Options for [submitMessageFeedback]. `messageId` identifies the chat
 * turn the user rated (either the agent turn's own id or the client-
 * generated prompt id — the caller decides which is the useful key for
 * their downstream analytics). `comment` is optional free-text carried
 * on `feedbackText`.
 */
export interface SubmitMessageFeedbackOptions {
    readonly messageId: string
    readonly rating: MessageFeedbackRating
    readonly comment?: string
}

/**
 * Submit a thumbs-up / thumbs-down rating for a chat agent message. Best-
 * effort — resolves once the mutation settles. Rejected promises are
 * caught by the caller (view stashes error handling via `errorHandler`).
 *
 * `up` → rating `5`, `down` → rating `1`. Keeps the numeric contract
 * of `Mutation.submitFeedback(rating: Float)` intact so the same field
 * can be reused if we ever grow a 1-5 star affordance.
 */
export async function submitMessageFeedback(options: SubmitMessageFeedbackOptions): Promise<void> {
    const { messageId, rating, comment } = options
    logDebug(target, 'submitMessageFeedback', { messageId, rating })

    if (isFeedStubEnabled()) {
        // Dev-fixture messages have no matching Firefly entity — the
        // toast + optimistic button state is the whole contract in
        // this mode. Bail before we build the gql request.
        return
    }

    if (!getAuth().isSignedIn()) {
        // Anonymous users can't own feedback state server-side. Keep
        // the local UX (button press + toast); the network hop would
        // only produce a `Not authenticated` error.
        logWarn(target, 'skipping submitFeedback — not signed in', { messageId })
        return
    }

    const trimmedComment = comment?.trim()
    try {
        await gqlFetch(submitFeedbackMutation, {
            id: messageId,
            rating: rating === 'up' ? 5 : 1,
            feedbackText: trimmedComment && trimmedComment.length > 0 ? trimmedComment : null,
        })
    } catch (err) {
        if (isKnownStubRejectionWith(err, STUB_REJECTION_RULES)) {
            // Dev-stub / unknown ids legitimately fail server-side. Log
            // at warn so the occurrence still lands in telemetry, but
            // don't rethrow — the caller shouldn't surface a "couldn't
            // save" toast for a non-user-actionable failure.
            logWarn(target, 'submitFeedback rejected id (stub / unknown), swallowing', {
                messageId,
                error: errorMessage(err),
            })
            return
        }
        throw asError(err)
    }
}
